// src/components/LayerPanel/AoisTab.jsx
//
// AOIS scope — a list of two-line AOI cards with a per-tab search that filters
// by name. This is the "browse" view, so the cards are spacious. Clicking a
// card toggles it as the current AOI (useAoiStore.selectedAoiId, the
// LayerPanel map-visibility selection — the same concept the panel header
// reflects). The current AOI is given a tinted background + border and a
// "Current" badge.
//
// This is the LayerPanel domain only: it drives map visibility via
// selectedAoiId and is independent of AoiPanel's editing state (activeAoiId).

import { useMemo, useState } from "react";
import { Search, X, LocateFixed, Plus, Settings } from "lucide-react";
import { useAoiStore } from "../../stores/useAoiStore";
import { useMapActions } from "../../stores/useMapActions";
import { useAppNav } from "../../hooks/useAppNav";
import { useOperatingAoiContext } from "../../stores/useOperatingAoiContext";
import { aoiBoundsFlat, bboxesIntersect } from "../../utils/aoiBounds";

function cx(...c) { return c.filter(Boolean).join(" "); }

// Second-line metadata from whatever the AOI record actually carries — area
// (from area_m2) and run count. Either part is shown only when present; if
// neither exists, returns null and the card omits the line entirely.
function aoiMetaLine(aoi) {
  const parts = [];
  if (aoi.area_m2 != null) {
    const acres = aoi.area_m2 / 4046.8564224;
    const sqMiles = aoi.area_m2 / 2589988.110336;
    parts.push(
      `${acres.toLocaleString(undefined, { maximumFractionDigits: acres < 10 ? 1 : 0 })} acres`
    );
    parts.push(
      `${sqMiles.toLocaleString(undefined, { maximumFractionDigits: 2 })} sq mi`
    );
  }
  if (aoi.run_count) {
    parts.push(`${aoi.run_count} ${aoi.run_count === 1 ? "run" : "runs"}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

export default function AoisTab() {
  const aois = useAoiStore((s) => s.aois);
  const selectedAoiId = useAoiStore((s) => s.selectedAoiId);
  const selectAoiForMap = useAoiStore((s) => s.selectAoiForMap);
  // Day 9 Path A — intent flag + activeAoiId reconciliation. "+ New"
  // clears any prior editor selection (clean-state signal per decision
  // 7); "Manage" preserves it so the user picks up where they left off.
  const setEditorEntryIntent = useAoiStore((s) => s.setEditorEntryIntent);
  const setActiveAoi = useAoiStore((s) => s.setActiveAoi);
  const exitAoiPolygonEdit = useAoiStore((s) => s.exitAoiPolygonEdit);
  const zoomToBounds = useMapActions((s) => s.zoomToBounds);
  const { setMapMode } = useAppNav();

  // Operating-AOI bbox for the intersect hint. The list endpoint already
  // returns each operating AOI's bbox as a flat [w, s, e, n] (the bounds
  // column is double precision[]), so no geometry fetch is needed here.
  const activeOperatingAoiId = useOperatingAoiContext((s) => s.activeOperatingAoiId);
  const operatingAois = useOperatingAoiContext((s) => s.operatingAois);
  const activeOperatingAoiBbox = activeOperatingAoiId
    ? operatingAois.find((a) => a.id === activeOperatingAoiId)?.bounds ?? null
    : null;

  const [search, setSearch] = useState("");

  const q = search.trim().toLowerCase();
  const searchedAois = q
    ? aois.filter((a) => (a.name ?? "").toLowerCase().includes(q))
    : aois;

  // Aggressive operating-AOI filter — when a workbench is active, only
  // AOIs whose bbox intersects the workbench bbox are shown. The
  // remaining AOIs are reported in a footer count ("N AOI(s) in other
  // workbenches") so the operator knows their workbench is filtering,
  // not data missing. When no workbench is active, no filtering — the
  // open state shows every project AOI.
  const { visibleAois, hiddenCount } = useMemo(() => {
    if (!activeOperatingAoiBbox) {
      return { visibleAois: searchedAois, hiddenCount: 0 };
    }
    const visible = [];
    let hidden = 0;
    for (const a of searchedAois) {
      if (bboxesIntersect(aoiBoundsFlat(a.geom), activeOperatingAoiBbox)) {
        visible.push(a);
      } else {
        hidden += 1;
      }
    }
    return { visibleAois: visible, hiddenCount: hidden };
  }, [searchedAois, activeOperatingAoiBbox]);

  // Shared "+ New" entry — clears any prior editor selection and switches to
  // AOI mode with the "new" intent. Used by both the populated toolbar and the
  // empty state, so a project with zero AOIs can still create its first one.
  const handleNewAoi = () => {
    exitAoiPolygonEdit();
    setActiveAoi(null);
    setEditorEntryIntent("new");
    setMapMode("aoi");
  };

  if (!aois.length) {
    return (
      <div className="px-2 py-2 space-y-2">
        <div className="px-2 pt-1 text-[10px] text-fg-secondary">
          No AOIs defined for this project.
        </div>
        <button
          type="button"
          onClick={handleNewAoi}
          title="Create a new AOI"
          className="w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded-md border border-slate-700 text-[10px] text-gray-300 hover:text-gray-100 hover:border-slate-500 transition"
        >
          <Plus size={11} />
          New AOI
        </button>
      </div>
    );
  }

  return (
    <div className="px-2 py-2 space-y-2">
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Search
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-secondary pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search AOIs…"
            aria-label="Search AOIs"
            className={cx(
              "w-full pl-6 pr-6 py-1.5 rounded-md border bg-bg text-[11px] text-gray-200",
              "placeholder-gray-600 focus:outline-none transition",
              search
                ? "border-accent-info/60 focus:border-accent-info"
                : "border-slate-700 focus:border-accent-info/60"
            )}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-secondary hover:text-gray-200"
            >
              <X size={11} />
            </button>
          )}
        </div>

        {/* Day 9 Path A — two panel-transition buttons. Both switch
            mapMode to "aoi" (revealing AoiPanel). The intent flag tells
            AoiPanel whether to focus + flash the name input ("new"
            path) or render normally ("manage" path). "+ New" also
            clears any prior activeAoiId for a clean editor entry. */}
        <button
          type="button"
          onClick={handleNewAoi}
          title="Create a new AOI"
          className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 rounded-md border border-slate-700 text-[10px] text-gray-300 hover:text-gray-100 hover:border-slate-500 transition"
        >
          <Plus size={11} />
          New
        </button>
        <button
          type="button"
          onClick={() => {
            // 3b — carry the browse selection into the editor so the AOI the
            // user picked here becomes the active (edited) AOI, instead of
            // opening on a stale activeAoiId. Only override when there's an
            // explicit selection, preserving "pick up where you left off" when
            // nothing is selected. Exit any stale vertex-edit so the editor
            // opens with default bbox-transform handles (Day-9 split intact).
            if (selectedAoiId) {
              setActiveAoi(selectedAoiId);
              exitAoiPolygonEdit();
            }
            setEditorEntryIntent("manage");
            setMapMode("aoi");
          }}
          title="Open the AOI editor"
          className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 rounded-md border border-slate-700 text-[10px] text-gray-300 hover:text-gray-100 hover:border-slate-500 transition"
        >
          <Settings size={11} />
          Manage
        </button>
      </div>

      {visibleAois.length === 0 ? (
        <div className="px-1 py-3 text-[10px] text-fg-secondary">
          {q
            ? "No AOIs match your search."
            : activeOperatingAoiBbox
              ? "No AOIs in this operating AOI."
              : "No AOIs defined for this project."}
        </div>
      ) : (
        <div className="space-y-1.5">
          {visibleAois.map((aoi) => {
            const isSelected = aoi.id === selectedAoiId;
            const bounds = aoiBoundsFlat(aoi.geom);
            const hasBounds = !!bounds;
            const metaLine = aoiMetaLine(aoi);

            return (
              <div
                key={aoi.id}
                onClick={() => selectAoiForMap(aoi.id)}
                title={isSelected ? "Click to deselect" : "Click to set as current AOI"}
                className={cx(
                  "group rounded-md border px-3 py-2 transition cursor-pointer",
                  isSelected
                    ? "border-accent-action/30"
                    : "border-slate-800 bg-bg hover:border-slate-700"
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className={cx(
                          "text-[13px] font-medium truncate",
                          isSelected
                            ? "text-gray-100"
                            : "text-gray-200 group-hover:text-gray-100"
                        )}
                      >
                        {aoi.name}
                      </span>
                    </div>
                    {metaLine && (
                      <div className="text-[11px] text-fg-secondary mt-0.5 truncate">
                        {metaLine}
                      </div>
                    )}
                  </div>

                  {/* Top-right: hover-revealed zoom-to, then the Current badge. */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasBounds) zoomToBounds?.(bounds);
                      }}
                      disabled={!hasBounds}
                      title={hasBounds ? "Zoom to AOI" : "No geometry"}
                      className={cx(
                        "p-0.5 rounded transition",
                        hasBounds
                          ? "opacity-0 group-hover:opacity-100 text-fg-secondary hover:text-accent-action hover:bg-accent-action/10"
                          : "opacity-20 cursor-not-allowed text-gray-700"
                      )}
                    >
                      <LocateFixed size={11} />
                    </button>
                    {isSelected && (
                      <span className="text-[8px] font-mono px-1 py-[1px] rounded bg-accent-info/15 text-accent-info border border-accent-info/25">
                        Current
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter footer — informational only, no interaction. Renders when
          a workbench is active and at least one AOI is hidden by its
          bbox not intersecting the workbench bbox. */}
      {hiddenCount > 0 && (
        <div className="px-1 pt-2 text-[10px] text-fg-secondary italic">
          {hiddenCount} AOI{hiddenCount === 1 ? "" : "s"} in other workbenches.
        </div>
      )}
    </div>
  );
}
